import React, { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Input, Stack, Typography } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import { EntityMediaFragment } from '@/features/messages/graphql/fragments/generated/MediaType';
import {
  MediaContainerComponent,
  UploadButton,
} from '@/modules/corporate/modules/course/views/CourseBuilder/components/SidebarDetails/Settings/components/Dialogs/components/Instructors/components/Instructors/UploadRenderers';
import MediaUpload from '@/modules/corporate/modules/course/views/CourseBuilder/components/SidebarDetails/Settings/components/MediaUpload';
import { ArticleAuthorType, ArticleContentType, MediaKindEnum } from '@/types/schema';

import { DialogProps } from '../../types';

const dataFields = {
  name: { 'data-field': 'name' },
};

// This is only used in Article Builder.
const AuthorDialog: React.FC<DialogProps> = ({ content, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const article = content as ArticleContentType;
  const [media, setMedia] = useState<EntityMediaFragment[]>([
    {
      url: article.author.picture,
      name: article.author.name,
      mediaId: article.author.name,
      kind: MediaKindEnum.Image,
    },
  ]);
  const [author, setAuthor] = useState<ArticleAuthorType>(article.author);

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const field = event.target.getAttribute('data-field');
      const value = event.target.value;
      setAuthor({ ...author, [field as string]: value });
    },
    [author, setAuthor],
  );

  const onMediaChange = useCallback(
    (mediaItem: EntityMediaFragment[]) => {
      setMedia(mediaItem);
      setAuthor({ ...author, picture: mediaItem[0]?.url! || '' });
    },
    [setAuthor, author],
  );

  const onAuthorSubmit = useCallback(() => {
    onSubmit?.({ author });
    onClose();
  }, [onSubmit, author, onClose]);

  return (
    <Modal
      action={{
        text: t('Admin:Form.AddAuthor'),
        onSubmit: onAuthorSubmit,
        disabled: author.picture === '' || author.name === '',
      }}
      classes={{
        paper: 'max-w-448',
      }}
      header={{
        title: t('Admin:Form.Author'),
      }}
      onClose={onClose}>
      <Stack alignItems="center" className="p-20" direction="row">
        <MediaUpload
          hideButtonOnFileAvailable
          CustomMediaContainer={MediaContainerComponent}
          CustomUploadButtonComponent={UploadButton}
          value={media}
          onChange={onMediaChange}
        />
        <Stack className="w-full flex-1 ms-16" direction="col">
          <Typography variant="body-short-02">{t('Common:Name')}</Typography>
          <Input
            className="mt-12 w-full"
            inputProps={dataFields.name}
            placeholder={t('Admin:Form.NameOfAuthorPlaceholder')}
            value={author.name}
            onChange={onInputChange}
          />
        </Stack>
      </Stack>
    </Modal>
  );
};

export default AuthorDialog;
